"use client";

import { OffersOffer, PaginationType } from '@/services/dataTypes';
import React, { useEffect, useState } from 'react';
import { filterOfferBanners, getBannerDimensions } from '@/constants/hooks';
import { apiOfferBanners } from '@/apis/offers';
import Banner from './Banner';

interface Props {
    bannerResponse: OffersOffer[];
    domain: string;
    mer_slug: string;
    slug_type: string;
    merchantId: string;
    companyId: string;
    pagination: PaginationType;
}

const VerticalOfferBanner = ({ bannerResponse, domain, mer_slug, slug_type, merchantId, companyId, pagination }: Props) => {
    const [banners, setBanners] = useState<OffersOffer[]>([]);
    const [page, setPage] = useState(2);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        setBanners(filterOfferBanners(bannerResponse, 20, 2000, 20, 2000));
        if (pagination?.next_page === null) setHasMore(false);
    }, [bannerResponse]);

    const loadMore = async () => {
        if (loading || !hasMore) return;
        setLoading(true);
        try {
            const res = await apiOfferBanners(merchantId, companyId, page);
            const newBanners = filterOfferBanners(res.data?.offers || [], 20, 2000, 20, 2000);
            setBanners(prev => [...prev, ...newBanners]);
            if (res?.data?.pagination?.next_page) { setPage(p => p + 1); setIsExpanded(true); }
            else setHasMore(false);
        } catch { /* silent */ }
        finally { setLoading(false); }
    };

    const showLess = () => {
        setBanners(filterOfferBanners(bannerResponse, 20, 2000, 20, 2000));
        setPage(2); setHasMore(true); setIsExpanded(false);
    };

    if (!banners?.length) return null;

    return (
        <div
            className="overflow-hidden"
            style={{
                background: '#081C11',
                clipPath: 'polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px))',
                border: '1px solid rgba(122,252,32,0.12)',
            }}
        >
            {/* Header */}
            <div className="px-6 pt-6 pb-4" style={{ borderBottom: '1px solid rgba(122,252,32,0.08)' }}>
                <div className="flex items-center gap-3 mb-1">
                    <div className="h-px w-6 bg-gradient-to-r from-[#7AFC20] to-transparent" />
                    <span className="text-[10px] font-black uppercase tracking-[0.3em]" style={{ color: 'rgba(122,252,32,0.5)' }}>
                        Exclusive
                    </span>
                </div>
                <h4 className="text-base font-black m-0" style={{ color: '#F5F5DC' }}>
                    Banner Offers
                </h4>
            </div>

            {/* Banners */}
            <div className="p-4 flex flex-col gap-3">
                {banners.map((offer_data, i) => {
                    const dimension = getBannerDimensions(offer_data);
                    return (
                        <div key={i} className="overflow-hidden transition-transform duration-300 hover:-translate-y-0.5"
                            style={{ border: '1px solid rgba(122,252,32,0.08)' }}>
                            <Banner
                                data={offer_data}
                                height={dimension?.height}
                                mer_slug={mer_slug}
                                slug_type={slug_type}
                                domain={domain}
                                width={dimension?.width}
                            />
                        </div>
                    );
                })}
            </div>

            {/* Controls */}
            {(hasMore || isExpanded) && (
                <div className="px-4 pb-4 flex items-center gap-3">
                    {hasMore && (
                        <button
                            onClick={loadMore}
                            disabled={loading}
                            className="flex-1 flex items-center justify-center gap-2 py-2.5 text-[10px] font-black uppercase tracking-widest transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50"
                            style={{
                                background: '#7AFC20',
                                color: '#081C11',
                                clipPath: 'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                            }}
                        >
                            {loading ? (
                                <span className="w-3.5 h-3.5 border-2 border-[#081C11] border-t-transparent rounded-full animate-spin" />
                            ) : 'Show More'}
                        </button>
                    )}
                    {isExpanded && (
                        <button
                            onClick={showLess}
                            className="flex-1 flex items-center justify-center py-2.5 text-[10px] font-black uppercase tracking-widest transition-all duration-300 hover:-translate-y-0.5"
                            style={{
                                background: 'rgba(122,252,32,0.06)',
                                border: '1px solid rgba(122,252,32,0.2)',
                                color: 'rgba(122,252,32,0.6)',
                                clipPath: 'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                            }}
                        >
                            Show Less
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default VerticalOfferBanner;
